import axios from '@/utils/request';
import { persistHasDonation, clearHasDonationFlag } from '@/utils/entStatus';
import { unwrapPayload } from '@/views/donation/format';

const PREFIX = '/ent_donation_manager';

let overviewCache = null;
let overviewPending = null;
let overviewSeq = 0;

/** 概览 4 指标。force 时忽略内存缓存（进捐赠管理页要刷新待办/累计）。 */
export function getDonationOverview(data = {}, options = {}) {
  const force = options && options.force;
  if (!force && overviewPending) return overviewPending;
  if (!force && overviewCache) return Promise.resolve(overviewCache);
  const seq = overviewSeq + 1;
  overviewSeq = seq;
  overviewPending = axios.post(`${PREFIX}/GetDonationOverview`, data)
    .then(res => {
      if (seq !== overviewSeq) return res;
      overviewCache = res;
      return res;
    })
    .finally(() => {
      if (seq === overviewSeq) overviewPending = null;
    });
  return overviewPending;
}

export function clearDonationOverviewCache() {
  overviewSeq += 1;
  overviewCache = null;
  overviewPending = null;
  yearListCache = null;
  yearListPending = null;
  yearListKey = '';
  presenceCache = null;
  presencePending = null;
}

export function resetDonationQueryState() {
  clearHasDonationFlag();
  clearDonationOverviewCache();
}

/** 捐赠列表（筛选 + 分页）。不返回 can_pay。 */
export function getDonationList(data, options) {
  return axios.post(`${PREFIX}/GetDonationList`, data, options);
}

let yearListCache = null;
let yearListPending = null;
let yearListKey = '';
let presenceCache = null;
let presencePending = null;

export function parseDonationListTotal(res) {
  const data = unwrapPayload(res);
  const total = Number(data.total || data.Total || 0);
  if (Number.isFinite(total) && total > 0) return total;
  const list = data.list || data.items || [];
  return list.length;
}

/** 捐赠管理入口：全量列表，total 非 0 才展示。 */
export function getDonationPresence() {
  if (presenceCache) return Promise.resolve(presenceCache);
  if (!presencePending) {
    presencePending = getDonationList({
      page: 1,
      page_size: 1,
    })
      .then(res => {
        presenceCache = res;
        persistHasDonation({ total: parseDonationListTotal(res) });
        return res;
      })
      .finally(() => {
        presencePending = null;
      });
  }
  return presencePending;
}

/** 侧栏「本年度捐赠」。菜单互切 Layout 会重挂，列表结果按年缓存。 */
export function getYearDonationList() {
  const year = new Date().getFullYear();
  const key = String(year);
  if (yearListCache && yearListKey === key) return Promise.resolve(yearListCache);
  if (!yearListPending) {
    yearListKey = key;
    yearListPending = getDonationList({
      page: 1,
      page_size: 100,
      donation_date_begin: `${year}-01-01`,
      donation_date_end: `${year}-12-31`,
    })
      .then(res => {
        yearListCache = res;
        return res;
      })
      .finally(() => {
        yearListPending = null;
      });
  }
  return yearListPending;
}

/** 捐赠详情。含 can_pay / can_pay_hint / 经办人 / 自有认领。 */
export function getDonationDetail(data, options) {
  return axios.post(`${PREFIX}/GetDonationDetail`, data, options);
}

/** 捐赠流水：付款流水 / 待支付订单 / 凭证。 */
export function getDonationFlow(data) {
  return axios.post(`${PREFIX}/GetDonationFlow`, data);
}

/** 去拨付善款，入参仅 cp_code。 */
export function createPaymentLink(data) {
  return axios.post(`${PREFIX}/CreatePaymentLink`, data);
}

/**
 * 查询某笔捐赠的开票信息（捐赠票据，TAPD 1069994386137089288）。
 *
 * 入参仅 cp_code —— 企业身份由后端从登录态 Header `Gy-H-Auth-Ent-Id` 解析。
 * ⚠️ 禁止传 ent_id：后端会忽略，传了反而误导后续维护者以为前端可以指定企业。
 */
export function getDonationInvoice(data) {
  return axios.post(`${PREFIX}/GetDonationInvoice`, data);
}

/**
 * 申请开票。
 *
 * ⚠️ 同上：禁止传 ent_id / 票据抬头 —— 抬头由后端取登录态企业的营业执照全名，
 * 保证票面主体与二要素核验通过的主体一致。
 *
 * transcode（可选）：取 GetDonationDetail 返回的 detail.transcode 原样回传，
 * 后端据此精确定位本单的可开票流水（不传则退化为按 cp_code 查全部）。
 * 它只是缩小查询范围的提示值，不是安全边界 —— 后端查流水恒带「仅未冻结未软删」
 * 过滤且恒以 cp_code 为分片键，传过期值最坏只会得到「暂无可开票流水」。
 */
export function applyDonationInvoice(data) {
  return axios.post(`${PREFIX}/ApplyDonationInvoice`, data);
}

/**
 * 查询本企业最近一次开票记录（TAPD 1069994386137089288）。
 *
 * 【用途】点击「去开票」时并行拉取，用于回填开票弹窗的 uscc / 邮箱 / 手机号 /
 * 收件人 / 收件地址等历史值，减少用户重复输入。
 *
 * 【安全约束】无入参 —— 企业身份由后端从登录态 Header `Gy-H-Auth-Ent-Id` 解析，
 * 前端不得传 ent_id。
 *
 * ⚠️ 【弱依赖】本接口失败/无数据都属正常（新企业首次开票即无历史），
 * 调用方必须做 catch 静默降级，绝不能因此阻塞开票弹窗弹出。
 */
export function getCorpRecentTicketInfo(data = {}) {
  return axios.post(`${PREFIX}/GetCorpRecentTicketInfo`, data);
}
